import React, { useState, useEffect } from "react";
import Sidebar from "../../../components/Dashboard/Sidebar/Sidebar";
import Header from "../../../components/Dashboard/Header/Header";
import SummaryCard from "../../../components/Dashboard/SummaryCard/SummaryCard";
import { 
  Calendar, 
  Clock, 
  UserCheck, 
  Clock9, 
  AlertCircle, 
  FileText, 
  Wallet 
} from 'lucide-react';
import Breadcrumbs from '../../../components/Common/Breadcrumbs/Breadcrumbs';
import styles from './EmployeeDashboard.module.css';
import Footer from '../../../components/Common/Footer/Footer';
import api from "../../../api/axios";

const EmployeeDashboard = () => {
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [summary, setSummary] = useState({
        total_attendance: 0,
        pending_leaves: 0,
        approved_leaves: 0,
        ot_hours: 0,
        advance_requests: 0,
        pending_tasks: 0
    });
    const [recentAttendance, setRecentAttendance] = useState([]);
    const [recentLeaves, setRecentLeaves] = useState([]);

    const user = JSON.parse(localStorage.getItem('user') || '{}');

    const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        setLoading(true);
        try {
            const response = await api.get('/employee-dashboard/stats');
            if (response.data.success) {
                setSummary(response.data.data.summary);
                setRecentAttendance(response.data.data.recent_attendance);
                setRecentLeaves(response.data.data.recent_leaves);
            }
        } catch (error) {
            console.error("Error fetching employee dashboard stats:", error);
        } finally {
            setLoading(false);
        }
    };

    const summaryData = [
        {
            icon: <UserCheck size={20} />,
            label: 'Attendance (This Month)',
            value: summary.total_attendance.toString(),
            iconBg: '#10b981'
        },
        {
            icon: <Calendar size={20} />,
            label: 'Pending Leaves',
            value: summary.pending_leaves.toString(),
            iconBg: '#f59e0b'
        },
        {
            icon: <Clock9 size={20} />,
            label: 'OT Hours (MTD)',
            value: summary.ot_hours.toString(),
            iconBg: '#3b82f6'
        },
        {
            icon: <Wallet size={20} />,
            label: 'Advance Requests',
            value: summary.advance_requests.toString(),
            iconBg: '#ef4444'
        },
        {
            icon: <FileText size={20} />,
            label: 'Pending Tasks',
            value: summary.pending_tasks.toString(),
            iconBg: '#6366f1'
        }
    ];

    return (
        <div className={`${styles['dashboard-container']} ${isSidebarOpen ? styles['sidebar-open'] : ''}`}>
            {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}
            <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

            <div className={styles['content-wrapper']}>
                <Header onMenuClick={toggleSidebar} />
                <main className={styles['main-content']}>
                    <Breadcrumbs customItems={[
                        { label: 'HR', path: '#' },
                        { label: 'Employee Dashboard', path: '/employees/dashboard' }
                    ]} />
                    
                    <div className={styles['dashboard-header-area']}>
                        <div className={styles['welcome-section']}>
                            <h1>Employee Dashboard</h1>
                            <p>Personal overview for {user?.name || 'Employee'}, {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}.</p>
                        </div>
                    </div>

                    <div className={styles['summary-grid']}>
                        {summaryData.map((data, index) => (
                            <SummaryCard key={index} {...data} />
                        ))}
                    </div>

                    <div className={styles['dashboard-grid']}>
                        <div className={styles['dashboard-card']}>
                            <div className={styles['card-header']}>
                                <div className={styles['header-left']}>
                                    <Clock size={20} />
                                    <h2>Recent Attendance</h2>
                                </div>
                                <button className={styles['view-all-btn']}>View All</button>
                            </div>
                            <div className={styles['card-content']}>
                                {recentAttendance && recentAttendance.length > 0 ? (
                                    <ul style={{ listStyle: 'none', padding: 0 }}>
                                        {recentAttendance.map(log => (
                                            <li key={log.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
                                                <span>{log.date}</span>
                                                <span style={{ color: log.status === 'Present' ? '#10b981' : '#ef4444' }}>{log.status}</span>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className={styles['empty-text']}>No recent attendance logs found.</p>
                                )}
                            </div>
                        </div>

                        <div className={styles['dashboard-card']}>
                            <div className={styles['card-header']}>
                                <div className={styles['header-left']}>
                                    <Calendar size={20} />
                                    <h2>My Leave Requests</h2>
                                </div>
                                <button className={styles['view-all-btn']}>View All</button>
                            </div>
                            <div className={styles['card-content']}>
                                {recentLeaves && recentLeaves.length > 0 ? (
                                    <ul style={{ listStyle: 'none', padding: 0 }}>
                                        {recentLeaves.map(leave => (
                                            <li key={leave.id} style={{ display: 'flex', flexDirection: 'column', padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <span style={{ fontWeight: 600 }}>{leave.leave_type?.name || 'Leave'}</span>
                                                    <span style={{ 
                                                        color: leave.status === 'approved' ? '#10b981' : leave.status === 'pending' ? '#f59e0b' : '#ef4444',
                                                        textTransform: 'capitalize',
                                                        fontWeight: 500,
                                                        fontSize: '0.85rem'
                                                    }}>{leave.status}</span>
                                                </div>
                                                <span style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '4px' }}>{leave.from_date} to {leave.to_date}</span>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className={styles['empty-text']}>No active leave requests.</p>
                                )}
                            </div>
                        </div>
                    </div>
                    <Footer />
                </main>
            </div>
        </div>
    );
};

export default EmployeeDashboard;
